import { useQuery } from '@tanstack/react-query';
import {
  apiGetVehicleManufacturers,
  apiGetVehicleModels,
  apiGetVehicleYears,
  type VehicleManufacturer,
  type VehicleModel,
  type VehicleYear,
} from 'api/quote-marketplace';

interface UseVehicleLookupOptions {
  enabled?: boolean;
  manufacturerRef?: string;
  modelRef?: string;
}

interface UseVehicleLookupResult {
  manufacturers: VehicleManufacturer[];
  models: VehicleModel[];
  years: VehicleYear[];
  isLoadingManufacturers: boolean;
  isLoadingModels: boolean;
  isLoadingYears: boolean;
}

export const useVehicleLookup = ({
  enabled = false,
  manufacturerRef,
  modelRef,
}: UseVehicleLookupOptions = {}): UseVehicleLookupResult => {
  const { data: manufacturersData, isLoading: isLoadingManufacturers } =
    useQuery({
      queryKey: ['vehicleManufacturers'],
      queryFn: apiGetVehicleManufacturers,
      enabled,
      staleTime: 1000 * 60 * 60,
      gcTime: 1000 * 60 * 60 * 24,
    });

  const { data: modelsData, isLoading: isLoadingModels } = useQuery({
    queryKey: ['vehicleModels', manufacturerRef],
    queryFn: () => apiGetVehicleModels(manufacturerRef!),
    enabled: !!manufacturerRef,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });

  const { data: yearsData, isLoading: isLoadingYears } = useQuery({
    queryKey: ['vehicleYears', modelRef],
    queryFn: () => apiGetVehicleYears(modelRef!),
    enabled: !!modelRef,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  return {
    manufacturers: manufacturersData?.manufacturers ?? [],
    models: modelsData?.models ?? [],
    years: yearsData?.years ?? [],
    isLoadingManufacturers,
    isLoadingModels,
    isLoadingYears,
  };
};
